/**
 * Compose your Latch (2026-07)
 * ============================
 * Pairs with sections/latch-composer.liquid. The section renders a
 * JSON lookup parsed from the bundle products' titles; this element
 * wires the three radio groups (pendant / chain / metal) to it:
 *   - the matching bundle supplies the preview photo, price, PDP url
 *     and the variant added to the bag
 *   - options that cannot complete a valid combination given the two
 *     OTHER current choices are disabled
 *   - add-to-bag mirrors the theme's product form: POST to
 *     cart_add_url with the cart sections for morphing, then dispatch
 *     the theme CartAddEvent so the drawer opens with focus handling
 *     and bubble updates intact.
 */

import { CartAddEvent } from '@theme/events';

/**
 * Return focus to a control that was disabled while focused, unless focus
 * has since moved somewhere meaningful.
 * @param {HTMLElement | null | undefined} el
 */
function refocusIfDropped(el) {
  if (!el || !el.isConnected || el.hidden) return;
  const active = document.activeElement;
  if (active && active !== document.body && active !== el) return;
  el.focus();
}

class LatchComposer extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-lc-combos]');
    if (!dataEl) return;
    try {
      this.combos = JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      this.combos = [];
    }
    if (!this.combos.length) return;

    this.radios = Array.from(this.querySelectorAll('[data-lc-radio]'));
    this.imageEl = this.querySelector('[data-lc-image]');
    this.placeholderEl = this.querySelector('[data-lc-placeholder]');
    this.priceEl = this.querySelector('[data-lc-price]');
    this.unavailableEl = this.querySelector('[data-lc-unavailable]');
    this.addBtn = this.querySelector('[data-lc-add]');
    this.viewLink = this.querySelector('[data-lc-view]');
    this.errorEl = this.querySelector('[data-lc-error]');

    this.radios.forEach((r) => r.addEventListener('change', () => this.update()));
    if (this.addBtn) this.addBtn.addEventListener('click', () => this.addToBag());

    this.update();
  }

  selection() {
    const sel = { p: '', c: '', m: '' };
    this.radios.forEach((r) => {
      if (r.checked) sel[r.dataset.lcRadio] = r.value;
    });
    return sel;
  }

  match(sel) {
    return this.combos.find(
      (x) => x.p === sel.p && x.c === sel.c && (x.m === sel.m || (!x.m && !sel.m))
    );
  }

  update() {
    const sel = this.selection();

    // Disable options that cannot form a combination with the OTHER
    // two current choices (they remain visible — the grammar of the
    // system stays legible).
    this.radios.forEach((r) => {
      const test = Object.assign({}, sel);
      test[r.dataset.lcRadio] = r.value;
      r.disabled = !this.match(test);
    });

    const combo = this.match(sel);
    if (combo) {
      if (this.imageEl) {
        this.imageEl.src = combo.img;
        this.imageEl.alt = combo.title;
        this.imageEl.hidden = false;
      }
      if (this.placeholderEl) this.placeholderEl.hidden = true;
      if (this.priceEl) this.priceEl.textContent = combo.price;
      if (this.unavailableEl) this.unavailableEl.hidden = true;
      if (this.addBtn) {
        this.addBtn.hidden = !combo.avail;
        this.addBtn.dataset.variantId = combo.vid;
      }
      if (this.viewLink) {
        this.viewLink.href = combo.url;
        this.viewLink.hidden = false;
      }
      if (!combo.avail && this.unavailableEl) this.unavailableEl.hidden = false;
    } else {
      if (this.imageEl) this.imageEl.hidden = true;
      if (this.placeholderEl) this.placeholderEl.hidden = false;
      if (this.priceEl) this.priceEl.textContent = '';
      if (this.unavailableEl) this.unavailableEl.hidden = false;
      if (this.addBtn) this.addBtn.hidden = true;
      if (this.viewLink) this.viewLink.hidden = true;
    }
  }

  async addToBag() {
    const variantId = this.addBtn && this.addBtn.dataset.variantId;
    if (!variantId) return;
    const addHadFocus = document.activeElement === this.addBtn;
    this.addBtn.disabled = true;
    this.showError('');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    // Mirror the product form: request the cart sections so listeners
    // can morph the bubble/drawer contents.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const res = await fetch((window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      });
      const response = await res.json();
      if (response.status) {
        // Shopify returns a translated reason (sold out, limit reached).
        this.showError(typeof response.description === 'string' && response.description ? response.description : this.genericError());
      } else {
        this.dispatchEvent(
          new CartAddEvent({}, variantId, {
            source: 'product-form-component',
            itemCount: 1,
            variantId: variantId,
            sections: response.sections,
          })
        );
      }
    } catch (e) {
      this.showError(this.genericError());
    } finally {
      this.addBtn.disabled = false;
      if (addHadFocus) refocusIfDropped(this.addBtn);
    }
  }

  genericError() {
    return (window.Theme && Theme.translations && Theme.translations.cart_error_generic) || '';
  }

  /**
   * Shows (or clears, with an empty string) the add-to-bag error under the
   * button. The element is role="alert", so the message is announced.
   * @param {string} message
   */
  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }
}

if (!customElements.get('latch-composer')) {
  customElements.define('latch-composer', LatchComposer);
}
